import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { useUserContext } from "../../../context/UserProvider";
import { db } from "../../../firebase";
import ProfilePicture from "../../util/ProfilePicture";
import { ChevronLeftIcon, CloseIcon, PaperPlaneIcon, UsersIcon } from "../../util/Icons";
import {
  formatMessageDateLabel,
  getEligibleTeamChats,
  getMessageDateKey,
} from "./teamChatUtils";

const CHAT_MESSAGE_CACHE_LIMIT = 25;
const CHAT_MESSAGE_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const OLDER_MESSAGE_BATCH_SIZE = 15;

function getDisplayName(userDetails, user) {
  return [userDetails?.firstName, userDetails?.lastName].filter(Boolean).join(" ") || user?.displayName || user?.email?.split("@")[0] || "Teammate";
}

function getChatDocPayload(chat) {
  return {
    chatKey: chat.chatKey,
    raceId: chat.raceId,
    legId: chat.legId,
    teamName: chat.teamName,
    raceName: chat.raceName,
    legName: chat.legName,
  };
}

function formatMessageTime(message) {
  const date = typeof message?.createdAt?.toDate === "function"
    ? message.createdAt.toDate()
    : new Date(message?.epochTime || Date.now());

  if (!Number.isFinite(date.getTime())) return "";

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function getMemberDisplayName(member) {
  return [member?.firstName, member?.lastName].filter(Boolean).join(" ") || member?.displayName || member?.name || "Teammate";
}

function getChatMessageCacheKey(chatId) {
  return `team_chat_messages:${chatId}`;
}

function getMessageEpochTime(message) {
  const epochTime = Number(message?.epochTime || 0);
  if (Number.isFinite(epochTime) && epochTime > 0) return epochTime;

  const createdAtTime = typeof message?.createdAt?.toMillis === "function" ? message.createdAt.toMillis() : 0;
  return Number.isFinite(createdAtTime) ? createdAtTime : 0;
}

function getCachedChatMessages(chatId) {
  try {
    const rawCache = window.localStorage.getItem(getChatMessageCacheKey(chatId));
    if (!rawCache) return [];

    const cache = JSON.parse(rawCache);
    if (!cache?.cachedAt || Date.now() - cache.cachedAt > CHAT_MESSAGE_CACHE_TTL_MS) {
      window.localStorage.removeItem(getChatMessageCacheKey(chatId));
      return [];
    }

    if (!Array.isArray(cache.messages)) return [];
    return cache.messages
      .filter((message) => message?.id && typeof message?.text === "string")
      .slice(-CHAT_MESSAGE_CACHE_LIMIT);
  } catch (error) {
    console.warn("Unable to read cached team chat messages", error);
    return [];
  }
}

function getCacheableMessage(message) {
  return {
    id: message.id,
    authorId: message.authorId || "",
    authorName: message.authorName || "",
    text: message.text || "",
    epochTime: getMessageEpochTime(message),
    editedAt: message.editedAt || null,
    deletedAt: message.deletedAt || null,
  };
}

function writeCachedChatMessages(chatId, messages) {
  try {
    const cacheableMessages = messages
      .filter((message) => message?.id && !message.pending && typeof message?.text === "string")
      .map(getCacheableMessage)
      .slice(-CHAT_MESSAGE_CACHE_LIMIT);

    window.localStorage.setItem(
      getChatMessageCacheKey(chatId),
      JSON.stringify({
        cachedAt: Date.now(),
        messages: cacheableMessages,
      })
    );
  } catch (error) {
    console.warn("Unable to cache team chat messages", error);
  }
}

function mergeChatMessages(currentMessages, nextMessages, maxMessages = null) {
  const messagesById = new Map();

  [...currentMessages, ...nextMessages].forEach((message) => {
    if (!message?.id) return;
    messagesById.set(message.id, {
      ...messagesById.get(message.id),
      ...message,
    });
  });

  const mergedMessages = Array.from(messagesById.values())
    .sort((a, b) => {
      const epochDiff = getMessageEpochTime(a) - getMessageEpochTime(b);
      if (epochDiff !== 0) return epochDiff;
      return String(a.id).localeCompare(String(b.id));
    });

  if (Number.isFinite(maxMessages) && maxMessages > 0) {
    return mergedMessages.slice(-maxMessages);
  }

  return mergedMessages;
}

function getNormalizedChatId(chatId) {
  if (!chatId) return "";

  let normalizedChatId = chatId;
  for (let i = 0; i < 3; i += 1) {
    try {
      const nextNormalizedChatId = decodeURIComponent(normalizedChatId);
      if (nextNormalizedChatId === normalizedChatId) break;
      normalizedChatId = nextNormalizedChatId;
    } catch (error) {
      break;
    }
  }

  return normalizedChatId;
}

export default function TeamChat() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user, userDetails, userRaces } = useUserContext();
  const teamChats = useMemo(() => getEligibleTeamChats(userRaces), [userRaces]);
  const selectedChat = useMemo(() => {
    if (teamChats.length === 0) return null;
    if (!chatId) return teamChats[0];
    const normalizedRouteChatId = getNormalizedChatId(chatId);
    return teamChats.find((chat) => getNormalizedChatId(chat.id) === normalizedRouteChatId) || teamChats[0];
  }, [chatId, teamChats]);
  const ensuredChatIds = useRef(new Set());
  const bottomRef = useRef(null);
  const messagesListRef = useRef(null);
  const messageInputRef = useRef(null);
  const skipNextAutoScrollRef = useRef(false);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [canLoadOlderMessages, setCanLoadOlderMessages] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [membersByChatId, setMembersByChatId] = useState({});
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");
  const latestMessageKey = useMemo(() => {
    const latestMessage = messages[messages.length - 1];
    if (!latestMessage) return "";
    return `${selectedChat?.id || ""}:${latestMessage.id}:${latestMessage.pending ? "pending" : "sent"}:${getMessageEpochTime(latestMessage)}`;
  }, [messages, selectedChat?.id]);

  const scrollToLatestMessage = useCallback((behavior = "smooth") => {
    window.requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior, block: "end" });
    });
  }, []);

  const ensureChatDoc = useCallback(async (chat) => {
    if (!chat?.id) return;
    if (ensuredChatIds.current.has(chat.id)) return;

    const chatRef = doc(db, "team_chats", chat.id);
    const chatSnap = await getDoc(chatRef);
    if (!chatSnap.exists()) {
      await setDoc(chatRef, {
        ...getChatDocPayload(chat),
        createdAt: serverTimestamp(),
      });
    } else {
      await setDoc(chatRef, getChatDocPayload(chat), { merge: true });
    }

    ensuredChatIds.current.add(chat.id);
  }, []);

  const markChatRead = useCallback(async (chat) => {
    if (!user?.uid || !chat?.id) return;

    await setDoc(
      doc(db, "users", user.uid, "chat_notifications", chat.id),
      {
        ...getChatDocPayload(chat),
        chatId: chat.id,
        unreadCount: 0,
        lastReadAt: serverTimestamp(),
      },
      { merge: true }
    );
  }, [user?.uid]);

  useEffect(() => {
    setErrorMsg("");
    if (!selectedChat?.id) {
      setMessages([]);
      setCanLoadOlderMessages(false);
      return undefined;
    }

    const cachedMessages = getCachedChatMessages(selectedChat.id);
    const latestCachedEpochTime = cachedMessages.reduce((latestEpochTime, message) => {
      return Math.max(latestEpochTime, getMessageEpochTime(message));
    }, 0);

    setMessages(cachedMessages);
    setCanLoadOlderMessages(cachedMessages.length >= CHAT_MESSAGE_CACHE_LIMIT);

    let isCancelled = false;
    let unsubscribeMessages = null;

    async function loadMessages() {
      try {
        await ensureChatDoc(selectedChat);
        if (isCancelled) return;

        const messagesCollection = collection(db, "team_chats", selectedChat.id, "messages");
        const messagesQuery = latestCachedEpochTime > 0
          ? query(
            messagesCollection,
            where("epochTime", ">", latestCachedEpochTime),
            orderBy("epochTime", "asc")
          )
          : query(
            messagesCollection,
            orderBy("epochTime", "asc"),
            limitToLast(CHAT_MESSAGE_CACHE_LIMIT)
          );

        unsubscribeMessages = onSnapshot(
          messagesQuery,
          (snap) => {
            const snapshotMessages = snap.docs.map((messageDoc) => ({
              id: messageDoc.id,
              pending: messageDoc.metadata.hasPendingWrites,
              ...messageDoc.data(),
            }));

            setMessages((currentMessages) => {
              const mergedMessages = mergeChatMessages(currentMessages, snapshotMessages);
              writeCachedChatMessages(selectedChat.id, mergedMessages);
              return mergedMessages;
            });

            if (!latestCachedEpochTime && snap.docs.length >= CHAT_MESSAGE_CACHE_LIMIT) {
              setCanLoadOlderMessages(true);
            }
          },
          (error) => {
            console.error("Unable to load team chat messages", error);
            if (!isCancelled) {
              setErrorMsg("Unable to load messages.");
            }
          }
        );
      } catch (error) {
        console.error("Unable to prepare team chat", error);
        if (!isCancelled) {
          setErrorMsg("Unable to load this team chat.");
        }
      }
    }

    loadMessages();

    return () => {
      isCancelled = true;
      if (unsubscribeMessages) {
        unsubscribeMessages();
      }
    };
  }, [ensureChatDoc, selectedChat]);

  useEffect(() => {
    if (!selectedChat?.id || !user?.uid) return undefined;

    markChatRead(selectedChat).catch((error) => {
      console.error("Unable to mark team chat read", error);
    });

    return onSnapshot(doc(db, "users", user.uid, "chat_notifications", selectedChat.id), (snap) => {
      const unreadCount = Number(snap.data()?.unreadCount || 0);
      if (unreadCount > 0) {
        markChatRead(selectedChat).catch((error) => {
          console.error("Unable to clear active team chat unread count", error);
        });
      }
    });
  }, [markChatRead, selectedChat, user?.uid]);

  useEffect(() => {
    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false;
      return;
    }

    scrollToLatestMessage(messages.length > 0 ? "smooth" : "auto");
  }, [latestMessageKey, messages.length, scrollToLatestMessage]);

  useEffect(() => {
    if (!messageInputRef.current) return;

    const maxInputHeight = 96;
    messageInputRef.current.style.height = "auto";
    messageInputRef.current.style.height = `${Math.min(messageInputRef.current.scrollHeight, maxInputHeight)}px`;
    messageInputRef.current.style.overflowY = messageInputRef.current.scrollHeight > maxInputHeight ? "auto" : "hidden";
  }, [messageText]);

  useEffect(() => {
    if (!showMembers || !selectedChat?.id || membersByChatId[selectedChat.id]) return undefined;

    let isCancelled = false;
    setMembersLoading(true);
    setMembersError("");

    async function loadTeamMembers() {
      try {
        const membersQuery = query(
          collection(db, "races", selectedChat.legId, "subscribedUsers"),
          where("teamName", "==", selectedChat.teamName)
        );
        const membersSnap = await getDocs(membersQuery);
        const members = membersSnap.docs
          .map((memberDoc) => ({
            id: memberDoc.id,
            ...memberDoc.data(),
          }))
          .sort((a, b) => getMemberDisplayName(a).localeCompare(getMemberDisplayName(b)));

        if (!isCancelled) {
          setMembersByChatId((prev) => ({ ...prev, [selectedChat.id]: members }));
        }
      } catch (error) {
        console.error("Unable to load team chat members", error);
        if (!isCancelled) {
          setMembersError("Unable to load team members.");
        }
      } finally {
        if (!isCancelled) {
          setMembersLoading(false);
        }
      }
    }

    loadTeamMembers();

    return () => {
      isCancelled = true;
    };
  }, [membersByChatId, selectedChat, showMembers]);

  useEffect(() => {
    if (!showMembers) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setShowMembers(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showMembers]);

  async function handleSendMessage(event) {
    event.preventDefault();
    if (isSending || !selectedChat?.id || !user?.uid) return;

    const text = messageText.trim();
    if (text.length < 1) return;

    setIsSending(true);
    setErrorMsg("");
    setMessageText("");

    try {
      await ensureChatDoc(selectedChat);
      const authorName = getDisplayName(userDetails, user);
      const chatRef = doc(db, "team_chats", selectedChat.id);
      const messageRef = doc(collection(db, "team_chats", selectedChat.id, "messages"));
      const batch = writeBatch(db);

      batch.set(messageRef, {
        authorId: user.uid,
        authorName,
        text,
        createdAt: serverTimestamp(),
        epochTime: Date.now(),
        editedAt: null,
        deletedAt: null,
      });

      scrollToLatestMessage();

      batch.set(
        chatRef,
        {
          ...getChatDocPayload(selectedChat),
          lastMessageAt: serverTimestamp(),
          lastMessageText: text.slice(0, 280),
          lastMessageAuthorId: user.uid,
          lastMessageAuthorName: authorName,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await batch.commit();
    } catch (error) {
      console.error("Unable to send team chat message", error);
      setMessageText(text);
      setErrorMsg("Message did not send. Please try again.");
    } finally {
      setIsSending(false);
    }
  }

  async function handleLoadOlderMessages() {
    if (!selectedChat?.id || isLoadingOlderMessages || messages.length === 0) return;

    const oldestVisibleEpochTime = messages.reduce((oldestEpochTime, message) => {
      const messageEpochTime = getMessageEpochTime(message);
      if (!messageEpochTime) return oldestEpochTime;
      return Math.min(oldestEpochTime, messageEpochTime);
    }, Infinity);

    if (!Number.isFinite(oldestVisibleEpochTime)) return;

    const messagesList = messagesListRef.current;
    const previousScrollHeight = messagesList?.scrollHeight || 0;
    const previousScrollTop = messagesList?.scrollTop || 0;

    setIsLoadingOlderMessages(true);
    setErrorMsg("");

    try {
      const olderMessagesQuery = query(
        collection(db, "team_chats", selectedChat.id, "messages"),
        where("epochTime", "<", oldestVisibleEpochTime),
        orderBy("epochTime", "desc"),
        limit(OLDER_MESSAGE_BATCH_SIZE)
      );
      const olderMessagesSnap = await getDocs(olderMessagesQuery);
      const olderMessages = olderMessagesSnap.docs.map((messageDoc) => ({
        id: messageDoc.id,
        pending: false,
        ...messageDoc.data(),
      }));

      skipNextAutoScrollRef.current = true;
      setMessages((currentMessages) => mergeChatMessages(currentMessages, olderMessages));
      setCanLoadOlderMessages(olderMessagesSnap.docs.length === OLDER_MESSAGE_BATCH_SIZE);

      window.requestAnimationFrame(() => {
        if (!messagesListRef.current) return;

        const nextScrollHeight = messagesListRef.current.scrollHeight;
        messagesListRef.current.scrollTop = previousScrollTop + (nextScrollHeight - previousScrollHeight);
      });
    } catch (error) {
      console.error("Unable to load older team chat messages", error);
      setErrorMsg("Unable to load older messages.");
    } finally {
      setIsLoadingOlderMessages(false);
    }
  }

  const teamMembers = selectedChat?.id ? membersByChatId[selectedChat.id] || [] : [];
  const memberCountLabel = teamMembers.length === 1 ? "1 member" : `${teamMembers.length} members`;

  if (teamChats.length === 0) {
    return (
      <main className="mx-auto flex min-h-[calc(100vh-12rem)] w-full max-w-3xl items-center pb-4">
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-8 text-center shadow-sm">
          <h1 className="mb-2 text-2xl font-bold text-slate-900">Team Chat</h1>
          <p className="m-0 text-sm font-medium text-slate-500">No team chats are available for your account.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto flex h-full min-h-0 w-full max-w-5xl flex-col pb-0 pt-3">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="grid grid-cols-[28px,1fr,32px] items-center gap-2 border-b border-slate-100 py-3 pl-2 pr-4">
          <Link
            to="/"
            aria-label="Back to main feed"
            title="Back to main feed"
            className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-950"
          >
            <ChevronLeftIcon size={20} color="currentColor" />
          </Link>
          <div className="min-w-0">
            <p className="m-0 truncate font-semibold tracking-wide text-slate-950">Team {selectedChat?.teamName} Chat</p>
            <p className="m-0 truncate text-xs font-normal text-slate-500">
              {[selectedChat?.raceName, selectedChat?.legName].filter(Boolean).join(" - ")}
            </p>
          </div>
          <button
            type="button"
            aria-label="View team members"
            title="View team members"
            onClick={() => setShowMembers(true)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-950"
          >
            <UsersIcon size={18} color="currentColor" />
          </button>
        </div>

        {teamChats.length > 1 && (
          <div className="flex gap-2 overflow-x-auto border-b border-slate-100 px-3 py-2">
            {teamChats.map((chat) => {
              const isSelected = chat.id === selectedChat?.id;
              return (
                <button
                  key={chat.id}
                  type="button"
                  className={
                    "flex shrink-0 items-center rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors " +
                    (isSelected
                      ? "border-[#006BC1] bg-[#006BC1] text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
                  }
                  onClick={() => navigate(`/team-chat/${encodeURIComponent(chat.id)}`)}
                >
                  {chat.teamName}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col bg-slate-50">
          <div ref={messagesListRef} className="min-h-0 flex-1 overscroll-contain overflow-y-auto px-3 py-4 md:px-4">
            {messages.length === 0 && (
              <div className="flex h-full items-center justify-center text-center">
                <p className="m-0 text-sm font-medium text-slate-400">No messages yet.</p>
              </div>
            )}

            {messages.length > 0 && canLoadOlderMessages && (
              <div className="mb-4 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadOlderMessages}
                  disabled={isLoadingOlderMessages}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:text-slate-300"
                >
                  {isLoadingOlderMessages ? "Loading..." : "Load earlier messages"}
                </button>
              </div>
            )}

            {messages.map((message, messageIndex) => {
              const isOwnMessage = message.authorId === user?.uid;
              const previousMessage = messages[messageIndex - 1];
              const showDateSeparator = !previousMessage
                || getMessageDateKey(previousMessage) !== getMessageDateKey(message);
              return (
                <div key={message.id}>
                  {showDateSeparator && (
                    <div
                      role="separator"
                      aria-label={formatMessageDateLabel(message)}
                      className="my-4 flex items-center gap-3 first:mt-0"
                    >
                      <div className="h-px flex-1 bg-slate-200" />
                      <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                        {formatMessageDateLabel(message)}
                      </span>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>
                  )}
                  <div
                    className={"mb-3 flex items-end gap-2 " + (isOwnMessage ? "justify-end" : "justify-start")}
                  >
                    {!isOwnMessage && (
                      <Link
                        to={`/profile/${message.authorId}`}
                        aria-label={`View ${message.authorName || "teammate"}'s profile`}
                        className="flex flex-shrink-0 no-underline"
                      >
                        <ProfilePicture
                          userID={message.authorId}
                          name={message.authorName}
                          className="!h-7 !w-7 text-[11px]"
                        />
                      </Link>
                    )}
                    <div className={"max-w-[78%] " + (isOwnMessage ? "text-right" : "text-left")}>
                      <div
                        className={
                          "inline-block rounded-2xl px-3 py-2 text-left text-sm leading-snug shadow-sm " +
                          (isOwnMessage
                            ? "rounded-br-md bg-[#006BC1] text-white"
                            : "rounded-bl-md bg-white text-slate-900")
                        }
                      >
                        {!isOwnMessage && (
                          <p className="mb-1 text-xs font-semibold leading-tight text-slate-500">{message.authorName || "Teammate"}</p>
                        )}
                        <p className="m-0 whitespace-pre-wrap break-words">{message.text}</p>
                        <p className={"mb-0 mt-1 text-right text-[11px] font-normal " + (isOwnMessage ? "text-blue-100" : "text-slate-400")}>
                          {message.pending ? "Sending..." : formatMessageTime(message)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          <form
            className="shrink-0 border-t border-slate-200 bg-white p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
            onSubmit={handleSendMessage}
          >
            {errorMsg && <p className="mb-2 text-sm font-semibold text-red-600">{errorMsg}</p>}
            <div className="grid grid-cols-[1fr,40px] items-center gap-2">
              <textarea
                ref={messageInputRef}
                value={messageText}
                onChange={(event) => setMessageText(event.target.value)}
                placeholder="Message your team"
                rows={1}
                maxLength={1000}
                className="min-h-[40px] max-h-24 resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm leading-tight focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                type="submit"
                aria-label="Send message"
                title="Send message"
                disabled={isSending || messageText.trim().length === 0}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#006BC1] text-white transition-colors hover:bg-[#00549a] disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                <PaperPlaneIcon size={18} color="currentColor" />
              </button>
            </div>
          </form>
        </div>
      </div>
      {showMembers && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-slate-950/40 px-3 pb-3 sm:items-center sm:justify-center sm:p-4"
          onClick={() => setShowMembers(false)}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-chat-members-title"
            className="max-h-[80vh] w-full overflow-hidden rounded-lg bg-white shadow-xl sm:max-w-md"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="grid grid-cols-[1fr,32px] items-center gap-3 border-b border-slate-100 px-4 py-3">
              <div className="min-w-0">
                <h2 id="team-chat-members-title" className="m-0 truncate text-base font-semibold text-slate-950">
                  {selectedChat?.teamName}
                </h2>
                <p className="m-0 text-xs font-normal text-slate-500">
                  {membersLoading ? "Loading members..." : memberCountLabel}
                </p>
              </div>
              <button
                type="button"
                aria-label="Close team members"
                title="Close"
                onClick={() => setShowMembers(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-950"
              >
                <CloseIcon size={16} color="currentColor" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-2">
              {membersError && <p className="m-2 text-sm font-semibold text-red-600">{membersError}</p>}
              {!membersError && membersLoading && (
                <div className="space-y-2 p-2">
                  {[0, 1, 2].map((item) => (
                    <div key={item} className="flex items-center gap-3 rounded-lg px-2 py-2">
                      <div className="h-8 w-8 rounded-full bg-slate-100" />
                      <div className="h-4 w-36 rounded bg-slate-100" />
                    </div>
                  ))}
                </div>
              )}
              {!membersError && !membersLoading && teamMembers.length === 0 && (
                <p className="m-2 text-sm font-medium text-slate-500">No team members found.</p>
              )}
              {!membersError && !membersLoading && teamMembers.map((member) => {
                const memberName = getMemberDisplayName(member);
                const isCurrentUser = member.id === user?.uid;
                return (
                  <Link
                    key={member.id}
                    to={`/profile/${member.id}`}
                    onClick={() => setShowMembers(false)}
                    className="grid grid-cols-[32px,1fr] items-center gap-3 rounded-lg px-2 py-2 text-slate-900 no-underline transition-colors hover:bg-slate-50 hover:no-underline"
                  >
                    <ProfilePicture userID={member.id} name={memberName} className="!h-8 !w-8 text-xs" />
                    <div className="min-w-0">
                      <p className="m-0 truncate text-sm font-semibold text-slate-800">
                        {memberName}
                        {isCurrentUser && <span className="ml-2 text-xs font-normal text-slate-400">You</span>}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
